import { Button, cn } from "@win-reality/react-ui";
import { Loader2, Moon, Sun } from "lucide-react";
import { ReactNode, useState } from "react";

import { ViewToggleButton } from "@/components/ui/ViewToggleButton";

import { GameHistoryStatus } from "../api/gameHistory";
import { TeamSide } from "../constants/teams";
import { useTaggingTheme } from "../hooks/useTaggingTheme";
import { GameFullResponse } from "../types/gameFull";
import { GameStateEdit, toBaserunnerFlags } from "../utils/pitchTagging";
import { PitchStatus } from "../utils/tagQueue";
import { TeamDisplays } from "../utils/teamColors";
import { abbreviateTeamName } from "../utils/teamName";
import { EditGameStateDialog } from "./EditGameStateDialog";
import { SetHomeTeamDialog } from "./EditGameStateDialog/SetHomeTeamDialog";
import { GameEndTime } from "./GameEndTime";
import { PlateAppearanceFeed } from "./PlateAppearanceFeed";
import { Scoreboard } from "./Scoreboard";
import { SIDEBAR_BUTTON_CLASS } from "./sidebarButton";

type GameSidebarProps = {
  data: GameFullResponse;
  teamDisplays: TeamDisplays;
  selectedActivityId?: string;
  selectedPlateAppearanceId?: string;
  /** Pitches whose tags are saving or queued */
  pitchStatuses?: ReadonlyMap<string, PitchStatus>;
  /** Loading of the pitches older than the first page */
  historyStatus?: GameHistoryStatus;
  /** Retries loading the older pitches after it failed */
  onLoadMoreHistory?: () => void;
  onSelectPitch: (activityId: string) => void;
  onSelectPlateAppearance: (plateAppearanceId: string) => void;
  onGameStateChange: (state: GameStateEdit) => void;
  /** Set Home Team; resolves to false if it failed */
  onChangeHomeTeam: (homeSide: TeamSide) => Promise<boolean>;
  onManageLineups: () => void;
  /** The SmartPark the game is on: its schedule is checked for conflicts */
  machineUuid: string;
  /** Moves the game's scheduled end; resolves to false if it failed */
  onChangeEndTime: (endTimeS: number) => Promise<boolean>;
  /** Opens End Game */
  onEndGame: () => void;
  children?: ReactNode;
  className?: string;
};

export const GameSidebar = ({
  data,
  teamDisplays,
  selectedActivityId,
  selectedPlateAppearanceId,
  pitchStatuses,
  historyStatus = "done",
  onLoadMoreHistory,
  onSelectPitch,
  onSelectPlateAppearance,
  onGameStateChange,
  onChangeHomeTeam,
  onManageLineups,
  machineUuid,
  onChangeEndTime,
  onEndGame,
  children,
  className,
}: GameSidebarProps) => {
  const { theme, toggleTheme } = useTaggingTheme();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHomeTeamOpen, setIsHomeTeamOpen] = useState(false);
  const [isSavingHomeTeam, setIsSavingHomeTeam] = useState(false);

  // The edit form's scores and names are stale after a swap, so a saved
  // change closes it too
  const saveHomeTeam = async (homeSide: TeamSide) => {
    setIsSavingHomeTeam(true);
    try {
      if (await onChangeHomeTeam(homeSide)) {
        setIsHomeTeamOpen(false);
        setIsEditModalOpen(false);
      }
    } finally {
      setIsSavingHomeTeam(false);
    }
  };

  // Live game state; Game applies saved tags for the latest pitch to it
  const { game } = data;
  const gameState = {
    awayTeamName: abbreviateTeamName(game.teams.away.name) || "AWAY",
    awayTeamColor: teamDisplays.away.color,
    awayScore: game.score.away,
    homeTeamName: abbreviateTeamName(game.teams.home.name) || "HOME",
    homeTeamColor: teamDisplays.home.color,
    homeScore: game.score.home,
    baserunners: toBaserunnerFlags(game.baseRunners),
    // The game service reports inning 0 until the first at bat starts
    inning: Math.max(game.inning, 1),
    isTopInning: game.isTopOfInning,
    balls: game.balls,
    strikes: game.strikes,
    outs: game.outs,
  };

  const handleSetTheme = (newTheme: "light" | "dark") => {
    if (theme !== newTheme) {
      toggleTheme();
    }
  };

  return (
    <div
      className={cn(
        // Size
        "w-[25%]",
        // Constraints
        "min-w-[320px] max-w-[500px]",
        // Height
        "h-full",
        // Layout
        "flex flex-col",
        // Background
        "bg-tagging-sidebar-bg dark:bg-tagging-sidebar-bg",
        // Effects: raised above the main area so its shadow falls on the
        // pitch header, which shares the sidebar's background
        "relative z-10 shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] transition-colors",
        className,
      )}
    >
      {/* Theme Toggle */}
      <div className="flex items-center justify-between p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Scorekeep
        </h3>
        <div className="flex gap-2">
          <ViewToggleButton
            data-testid="game-theme-toggle"
            data-value="light"
            aria-pressed={theme === "light"}
            isActive={theme === "light"}
            icon={Sun}
            onClick={() => handleSetTheme("light")}
            size="small"
          />
          <ViewToggleButton
            data-testid="game-theme-toggle"
            data-value="dark"
            aria-pressed={theme === "dark"}
            isActive={theme === "dark"}
            icon={Moon}
            onClick={() => handleSetTheme("dark")}
            size="small"
          />
        </div>
      </div>

      {/* Game state stays put while the at bat feed scrolls */}
      <div className="shrink-0 space-y-4 px-4 pb-4">
        <Scoreboard
          {...gameState}
          onEditClick={() => setIsEditModalOpen(true)}
        />
        <div className="space-y-3">
          <GameEndTime
            endTime={game.endTime}
            gameId={game.gameId}
            machineUuid={machineUuid}
            onChangeEndTime={onChangeEndTime}
            onEndGame={onEndGame}
          />
          <Button
            variant="secondary-flat-dark-outline"
            size="sm"
            onClick={onManageLineups}
            data-testid="lineup-manage-button"
            className={SIDEBAR_BUTTON_CLASS}
          >
            Manage Lineups
          </Button>
        </div>
      </div>

      <div className="tagging-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4">
        {/* Active At-Bat + Game History */}
        <PlateAppearanceFeed
          data={data}
          awayTeamColor={gameState.awayTeamColor}
          homeTeamColor={gameState.homeTeamColor}
          selectedActivityId={selectedActivityId}
          selectedPlateAppearanceId={selectedPlateAppearanceId}
          pitchStatuses={pitchStatuses}
          onSelectPitch={onSelectPitch}
          onAddDetails={onSelectPitch}
          onMatchupClick={onSelectPlateAppearance}
        />

        {/* Older pitches append below the history as they load */}
        <HistoryLoadStatus
          status={historyStatus}
          onLoadMore={onLoadMoreHistory}
        />

        {children}
      </div>

      {/* Edit Game State Modal */}
      <EditGameStateDialog
        isOpen={isEditModalOpen}
        {...gameState}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={(state) => {
          onGameStateChange(state);
          setIsEditModalOpen(false);
        }}
        onChangeTeams={() => setIsHomeTeamOpen(true)}
      />

      {/* Opens over Edit Game State; Cancel goes back to it */}
      {isHomeTeamOpen && (
        <SetHomeTeamDialog
          homeTeamName={game.teams.home.name || "Home"}
          awayTeamName={game.teams.away.name || "Away"}
          isSaving={isSavingHomeTeam}
          onCancel={() => setIsHomeTeamOpen(false)}
          onSave={(homeSide) => void saveHomeTeam(homeSide)}
        />
      )}
    </div>
  );
};

/** Below the feed while older pitches load, with a retry if that failed. */
const HistoryLoadStatus = ({
  status,
  onLoadMore,
}: {
  status: GameHistoryStatus;
  onLoadMore?: () => void;
}) => {
  if (status === "done") return null;
  if (status === "loading") {
    return (
      <div
        data-testid="plate-appearance-feed-history-status"
        data-status="loading"
        className="flex items-center justify-center gap-2 text-sm text-tagging-muted"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading earlier pitches…
      </div>
    );
  }
  return (
    <div
      data-testid="plate-appearance-feed-history-status"
      data-status="error"
      className="flex flex-col items-center gap-2 text-sm text-tagging-muted"
    >
      Couldn't load earlier pitches
      {onLoadMore && (
        <Button
          data-testid="plate-appearance-feed-history-retry-button"
          variant="secondary-flat-dark-outline"
          size="sm"
          onClick={onLoadMore}
          className="rounded-full"
        >
          Load more
        </Button>
      )}
    </div>
  );
};
